import { ref } from "vue";
import { renderPolylineAsLaserOnCanvas } from "@/components/common/annotation-view/components/laser-path";
import { renderPolylineAsLineOnCanvas } from "@/components/common/annotation-view/components/pencil-path";
import { CursorWithDrawingData } from "@/models";
import useCanvas from "./use-canvas";
import { Colors } from "@/utils/constant";

interface UseRealtimeDrawingOptions {
  defaultStrokeColor?: string;
}

export const useRealtimeDrawing = ({ defaultStrokeColor = Colors.Black }: UseRealtimeDrawingOptions) => {
  // Local state to accumulate drawing points
  const realtimePencilPaths = ref<any[]>([]);
  const realtimeLaserPaths = ref<{ lines: any[]; isDone: boolean }>({ lines: [], isDone: false });

  const handleDrawingData = (payload: CursorWithDrawingData) => {
    const [canvas] = useCanvas();
    if (!payload?.drawingData || !canvas) return;
    const { drawingData } = payload;

    if (drawingData.toolType === "laser") {
      handleLaserDrawing(payload);
    } else if (drawingData.toolType === "pencil") {
      handlePencilDrawing(payload);
    }
  };

  const handleLaserDrawing = (payload: CursorWithDrawingData) => {
    const { drawingData } = payload;
    if (!drawingData) return;

    // Accumulate laser points like SET_REALTIME_LASER_PATH mutation
    if (drawingData.isDone) {
      realtimeLaserPaths.value.isDone = true;
      realtimeLaserPaths.value.lines = [];
    } else {
      const index = realtimeLaserPaths.value.lines.findIndex((item: any) => item.id === drawingData.id);
      realtimeLaserPaths.value.isDone = false;

      if (index < 0) {
        // New line
        realtimeLaserPaths.value.lines.push({
          id: drawingData.id || "",
          points: [...(drawingData.pointsSkipped || []), { x: payload.x, y: payload.y }],
          strokeColor: drawingData.strokeColor || defaultStrokeColor,
          strokeWidth: drawingData.strokeWidth || 2,
        });
      } else {
        // Append to existing line
        realtimeLaserPaths.value.lines[index].points = [
          ...realtimeLaserPaths.value.lines[index].points,
          ...(drawingData.pointsSkipped || []),
          { x: payload.x, y: payload.y },
        ];
      }
    }

    // Render accumulated laser paths
    renderPolylineAsLaserOnCanvas({
      realtimeLaserPaths: realtimeLaserPaths,
    });
  };

  const handlePencilDrawing = (payload: CursorWithDrawingData) => {
    const { drawingData } = payload;
    if (!drawingData) return;

    // Accumulate pencil points like SET_REALTIME_PENCIL_PATH mutation
    const index = realtimePencilPaths.value.findIndex((item: any) => item.id === drawingData.id);

    if (index < 0) {
      // New path
      if (realtimePencilPaths.value.length && !realtimePencilPaths.value[realtimePencilPaths.value.length - 1].isDone) {
        realtimePencilPaths.value[realtimePencilPaths.value.length - 1].isDone = true;
      }
      realtimePencilPaths.value.push({
        id: drawingData.id || "",
        points: [...(drawingData.pointsSkipped || []), { x: payload.x, y: payload.y }],
        strokeColor: drawingData.strokeColor || defaultStrokeColor,
        strokeWidth: drawingData.strokeWidth || 2,
        isDone: drawingData.isDone || false,
        ratio: 1,
        lineIdRelated: drawingData.lineIdRelated || "",
        objectId: drawingData.objectId,
      });
    } else {
      // Append to existing path
      realtimePencilPaths.value[index].points = [
        ...realtimePencilPaths.value[index].points,
        ...(drawingData.pointsSkipped || []),
        { x: payload.x, y: payload.y },
      ];
      realtimePencilPaths.value[index].isDone = drawingData.isDone || false;
      realtimePencilPaths.value[index].objectId = drawingData.objectId;
    }

    // Render accumulated pencil paths
    renderPolylineAsLineOnCanvas(realtimePencilPaths.value);
  };

  const clearPencilPaths = () => {
    realtimePencilPaths.value = [];
  };

  const clearLaserPaths = () => {
    realtimeLaserPaths.value = { lines: [], isDone: false };
  };

  const clearAllPaths = () => {
    clearPencilPaths();
    clearLaserPaths();
  };

  const removePathsByObjectId = (objectId: string) => {
    if (!objectId) return;
    const [canvas] = useCanvas();
    const canRender = !!canvas;

    const nextPencilPaths = realtimePencilPaths.value.filter((path: any) => path.objectId !== objectId && path.id !== objectId);
    if (nextPencilPaths.length !== realtimePencilPaths.value.length) {
      realtimePencilPaths.value = nextPencilPaths;
      if (canRender) {
        renderPolylineAsLineOnCanvas(realtimePencilPaths.value);
      }
    }

    const nextLaserLines = realtimeLaserPaths.value.lines.filter((line: any) => line.id !== objectId && line.objectId !== objectId);
    if (nextLaserLines.length !== realtimeLaserPaths.value.lines.length) {
      realtimeLaserPaths.value.lines = nextLaserLines;
      realtimeLaserPaths.value.isDone = false;
      if (canRender) {
        renderPolylineAsLaserOnCanvas({ realtimeLaserPaths });
      }
    }
  };

  return {
    realtimePencilPaths,
    realtimeLaserPaths,
    handleDrawingData,
    handleLaserDrawing,
    handlePencilDrawing,
    clearPencilPaths,
    clearLaserPaths,
    clearAllPaths,
    removePathsByObjectId,
  };
};
